import React, { useEffect, useState, useRef, useMemo } from 'react'
import styles from './index.module.scss'
import { isIOS, isAndroid, Storage, validateIDNumber } from '@bihu/common-js'
import { Picker, CascadePicker, Input, Button, Toast } from 'antd-mobile'
import type { PickerValue, PickerValueExtend } from 'antd-mobile/es/components/picker'
import indexApi from '@/apis'
import { useSearchParams } from 'react-router-dom'
import { jobTypeOptions, jobTypesOptionsMap } from '@/pages/fill-information/options'
import { TOKEN, ENTER_AGREEMENT } from '@/constants/storage'
import classNames from 'classnames'
import activityApi from '@/apis/activity'
import { clearCachedParams, getCachedParamsAsQueryString } from '@/utils/login-params'
import NumberInput from '@/components/number-input/number-input'
import cityDate from '@/assets/js/city'
import { getSex, getAge } from '@/utils/utils'

interface Props {}

type AssetKey = 'housingFund' | 'socialSecurity' | 'houseProperty' | 'carProperty' | 'insurance'

const ASSET_OPTIONS: { key: AssetKey, label: string }[] = [
  { key: 'housingFund', label: '有公积金' },
  { key: 'socialSecurity', label: '有社保' },
  { key: 'houseProperty', label: '有房' },
  { key: 'carProperty', label: '有车' },
  { key: 'insurance', label: '有保险单' },
]

const HAS_VALUE = '2'
const NONE_VALUE = '0'
const COUNTDOWN_SECONDS = 3590
const padZero = (num: number) => (num < 10 ? `0${num}` : `${num}`)

// 填写资料（身份 + 城市 + 资产 合并一页）
const FillInformation: React.FC<Props> = () => {
  const [search] = useSearchParams()
  const [amount, setAmount] = useState<any>(30000)
  const [periodsValue, setPeriodsValue] = useState<any>('12')
  const [token, setToken] = useState('')
  const [job, setJob] = useState('')
  const [jobLabel, setJobLabel] = useState('')
  const [houseProperty, setHouseProperty] = useState('')
  const [carProperty, setCarProperty] = useState('')
  const [socialSecurity, setSocialSecurity] = useState('')
  const [housingFund, setHousingFund] = useState('')
  const [insurance, setInsurance] = useState('')
  const [realName, setRealName] = useState('')
  const [identity, setIdentity] = useState('')
  const [noneOfAbove, setNoneOfAbove] = useState(false)
  const [assetsExpanded, setAssetsExpanded] = useState(true)
  const [assetsDecided, setAssetsDecided] = useState(false)
  const [jobVisible, setJobVisible] = useState(false)
  const [periodsVisible, setPeriodsVisible] = useState(false)
  const [cityVisible, setCityVisible] = useState(false)
  const [cityValue, setCityValue] = useState<PickerValue[]>([])
  const [cityShow, setCityShow] = useState('')
  const [haveCityData, setHaveCityData] = useState(false)
  const [checked, setChecked] = useState(false)
  const [isShake, setIsShake] = useState(false)
  const [btnLoading, setBtnLoading] = useState(false)
  const [remainSeconds, setRemainSeconds] = useState(COUNTDOWN_SECONDS)
  const formLoginModalRef: any = useRef()
  const countdownRef = useRef<number>()
  const savingRef = useRef(false)
  const [adChannelCode, setAdChannelCode] = useState('')
  const [isHuaWeiPhone, setIsHuaWeiPhone] = useState(false)
  const [min] = useState(30000)
  let [max] = useState(200000)

  const hours = padZero(Math.floor(remainSeconds / 3600))
  const minutes = padZero(Math.floor((remainSeconds % 3600) / 60))
  const seconds = padZero(remainSeconds % 60)

  const professionList = [
    jobTypeOptions.map(item => ({
      label: item.label,
      name: 'job',
      value: item.value,
    })),
  ]
  const periodsColumns = [
    [
      { label: '6个月', value: '6' },
      { label: '9个月', value: '9' },
      { label: '12个月', value: '12' },
    ],
  ]

  const assetValues: Record<AssetKey, string> = {
    housingFund,
    socialSecurity,
    houseProperty,
    carProperty,
    insurance,
  }

  const setAssetValue = (key: AssetKey, value: string) => {
    switch (key) {
      case 'housingFund':
        setHousingFund(value)
        break
      case 'socialSecurity':
        setSocialSecurity(value)
        break
      case 'houseProperty':
        setHouseProperty(value)
        break
      case 'carProperty':
        setCarProperty(value)
        break
      case 'insurance':
        setInsurance(value)
        break
      default:
        break
    }
  }

  useEffect(() => {
    setIsHuaWeiPhone(navigator.userAgent.toLowerCase().indexOf('huawei') !== -1)
  }, [])

  useEffect(() => {
    const code = search.get('adChannelCode')
    if (code) {
      setAdChannelCode(code)
      localStorage.setItem('ad_channel_code', code)
      localStorage.setItem('hostName', '卫付通')
    }
    if (search.get('amount')) {
      setAmount(search.get('amount'))
    }
    if (search.get('currentPeriods')) {
      setPeriodsValue(search.get('currentPeriods'))
    }
  }, [search])

  useEffect(() => {
    countdownRef.current = window.setInterval(() => {
      setRemainSeconds(prev => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => {
      if (countdownRef.current) {
        window.clearInterval(countdownRef.current)
      }
    }
  }, [])

  useEffect(() => {
    async function checkMobileLogin(reqId: string) {
      const res = await indexApi.checkMobileLogin({ reqId })
      if (res) {
        const queryString = getCachedParamsAsQueryString()
        const loginRes = await activityApi.login({ upStreamUnionLoginReqId: reqId }, queryString)
        handleLoginSuccess(loginRes)
      } else {
        formLoginModalRef.current && formLoginModalRef.current.init({ reqId })
      }
    }
    const urlToken = search.get('token')
    const reqId = search.get('reqId')
    let tokenTemp = Storage.get(TOKEN)
    if (reqId) {
      checkMobileLogin(reqId)
    } else if (urlToken) {
      Storage.set(TOKEN, urlToken)
      setToken(urlToken)
      const url = window.location.href
      const updatedUrl = url.replace(/([?&])token=[^&]+(&|$)/, '$1')
      window.history.replaceState(null, 'null', updatedUrl)
    } else if (tokenTemp) {
      setToken(tokenTemp)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLoginSuccess = (res: any) => {
    Storage.set(TOKEN, res.token)
    setToken(res.token)
    clearCachedParams()
  }

  useEffect(() => {
    if (adChannelCode) {
      openLocation()
    }
  }, [adChannelCode])

  const openLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        position => {
          getUserLocation(position.coords.latitude, position.coords.longitude)
        },
        () => {
          getUserLocation(null, null)
        },
      )
    } else {
      getUserLocation(null, null)
    }
  }

  const getUserLocation = async(latitude: any, longitude: any) => {
    try {
      let params = latitude ? {
        location: `${latitude},${longitude}`,
        adChannelCode,
        coordtype: 'wgs84ll'
      } : { adChannelCode }
      let res: any = await activityApi.reverseGeo(params)
      let currentProvince: any = cityDate.find(item => item.label === res.data.province)
      let currentCity: any = ''
      if (currentProvince && currentProvince.children) {
        currentCity = currentProvince.children.find((cityItem: any) => cityItem.label === res.data.city)
      }
      if (currentProvince && currentCity && !haveCityData) {
        setCityValue(`${currentProvince.value},${currentCity.value}`.split(','))
        setCityShow(`${currentProvince.label},${currentCity.label}`)
        setHaveCityData(true)
      }
    } catch (error) {
      // 定位失败不阻断填写
    }
  }

  useEffect(() => {
    async function getApplyStatus() {
      try {
        const res: any = await indexApi.getApplyStatus()
        indexApi.reportUserBehavior(1)
        let devicePlatForm: any = 3
        if (isAndroid) {
          devicePlatForm = 1
        }
        if (isIOS) {
          devicePlatForm = 2
        }
        indexApi.reportPhoneType(devicePlatForm)
        fillData(res.data)
        if (res.data && res.data?.submitStatus === 1) {
          const submitResult: any = await indexApi.getFomrSubmitStatus({ adChannelCode })
          if (submitResult.productName === null) {
            window.location.replace(`/fill-information/success?options=${encodeURIComponent(JSON.stringify({
              isNotFormReq: true,
              rePageShowType: adChannelCode === '9o0w2B' && isHuaWeiPhone ? 2 : 0,
              revisit: true
            }))}`)
            return
          }
          if (submitResult.thirdpartyType === 5) {
            window.location.replace(`/fill-information/authorize?options=${encodeURIComponent(JSON.stringify(submitResult))}&adChannelCode=${adChannelCode}&isHuaWeiPhone=${isHuaWeiPhone}&applyLimit=${res.data.applyLimit}`)
            return
          }
          window.location.replace(`/fill-information/common-authorize?options=${encodeURIComponent(JSON.stringify({
            adChannelCode,
            isHuaWeiPhone,
            applyLimit: res.data.applyLimit,
            workCity: res.data.workCity,
            rqResult: submitResult
          }))}`)
        } else if (res.data && res.data?.submitStatus === 10) {
          const reMatchResult: any = await indexApi.getFomrSubmitStatus({ adChannelCode })
          if (reMatchResult.rematchResult === 3) {
            window.location.replace(`/fill-information/rematch-waiting?options=${encodeURIComponent(JSON.stringify({
              adChannelCode,
              isHuaWeiPhone,
              applyLimit: res.data.applyLimit,
              workCity: res.data.workCity,
              rqResult: reMatchResult
            }))}`)
            return
          }
          const tempOptions = JSON.parse(JSON.stringify(reMatchResult))
          tempOptions.isNotFormReq = true
          tempOptions.rePageShowType = adChannelCode === '9o0w2B' && isHuaWeiPhone ? 2 : 0
          tempOptions.revisit = true
          window.location.replace(`/fill-information/success?options=${encodeURIComponent(JSON.stringify(tempOptions))}`)
        }
      } catch (error) {
        console.log('error', error)
      }
    }

    if (token) {
      getApplyStatus()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const fillData = (init: any) => {
    if (!init) {
      return
    }
    if (init.realName) {
      setRealName(init.realName)
    }
    if (init.identity) {
      setIdentity(init.identity)
    }
    if (init.workCityCode || init.workCity) {
      setHaveCityData(true)
      if (init.workCityCode) {
        setCityValue(String(init.workCityCode).split(','))
      }
      setCityShow(init.workCity || '')
    }

    let hasAsset = false
    let allNone = true
    const applyAsset = (val: any, setter: (v: string) => void) => {
      if (val === null || val === undefined) {
        return
      }
      hasAsset = true
      if (val > 0) {
        setter(HAS_VALUE)
        allNone = false
        setAmount((prev: number) => Number(prev) + 5000)
      } else {
        setter(NONE_VALUE)
      }
    }
    applyAsset(init.houseProperty, setHouseProperty)
    applyAsset(init.carProperty, setCarProperty)
    applyAsset(init.socialSecurity, setSocialSecurity)
    applyAsset(init.housingFund, setHousingFund)
    applyAsset(init.insurance, setInsurance)
    if (hasAsset) {
      setAssetsDecided(true)
      setNoneOfAbove(allNone)
    }
    if (init.job !== null && init.job !== undefined) {
      const label = jobTypesOptionsMap[init.job]
      if (label) {
        setJob(String(init.job))
        setJobLabel(label)
        setAmount((prev: number) => Number(prev) + 5000)
      }
    }
  }

  const buildAssetParams = (override?: Partial<Record<AssetKey, string>>, forceNone?: boolean) => {
    const isNone = forceNone ?? noneOfAbove
    if (isNone) {
      return {
        houseProperty: NONE_VALUE,
        carProperty: NONE_VALUE,
        socialSecurity: NONE_VALUE,
        housingFund: NONE_VALUE,
        insurance: NONE_VALUE,
      }
    }
    const current = { ...assetValues, ...override }
    return {
      houseProperty: current.houseProperty === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
      carProperty: current.carProperty === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
      socialSecurity: current.socialSecurity === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
      housingFund: current.housingFund === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
      insurance: current.insurance === HAS_VALUE ? HAS_VALUE : NONE_VALUE,
    }
  }

  const tempSave = async(extra: Record<string, any> = {}) => {
    if (savingRef.current) {
      return
    }
    savingRef.current = true
    const params: any = {
      realName,
      identity,
      workCity: cityShow,
      workCityCode: cityValue.join(),
      job: job || undefined,
      ...(assetsDecided ? buildAssetParams() : {}),
      ...extra,
    }
    localStorage.setItem('fillInformation', JSON.stringify(params))
    try {
      await indexApi.tempSave(params)
    } catch (error) {
      // 自动暂存失败不阻断填写
    } finally {
      savingRef.current = false
    }
  }

  useEffect(() => {
    if (!realName && !identity && !cityShow && !job && !assetsDecided) {
      return
    }
    const timer = window.setTimeout(() => {
      tempSave()
    }, 500)
    return () => window.clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realName, identity, cityShow, cityValue, job, houseProperty, carProperty, socialSecurity, housingFund, insurance, noneOfAbove, assetsDecided])

  const hasSelectedAsset = (values: Record<AssetKey, string> = assetValues) => (
    Object.values(values).some(val => val === HAS_VALUE)
  )

  const handleAssetToggle = (key: AssetKey) => {
    const current = assetValues[key]
    const nextVal = current === HAS_VALUE ? '' : HAS_VALUE
    if (nextVal === HAS_VALUE && !current) {
      setAmount((prev: number) => Number(prev) + 5000)
    }
    const nextAssets = { ...assetValues, [key]: nextVal }
    setAssetValue(key, nextVal)
    setNoneOfAbove(false)
    // 至少选一项才算有效；全部取消则需再选「以上都没有」
    setAssetsDecided(hasSelectedAsset(nextAssets))
  }

  const handleNoneOfAbove = () => {
    setNoneOfAbove(true)
    setAssetsDecided(true)
    setHouseProperty(NONE_VALUE)
    setCarProperty(NONE_VALUE)
    setSocialSecurity(NONE_VALUE)
    setHousingFund(NONE_VALUE)
    setInsurance(NONE_VALUE)
  }

  const amountOnChange = (str: string) => {
    str = str.replace(/[^\d]/g, '')
    if (str && str !== '0') {
      setAmount(str)
    } else {
      setAmount('')
    }
  }

  const amountOnBlur = () => {
    let str = String(amount).replace(/[^\d]/g, '')
    max = 200000
    if (+str > max) {
      setAmount(max)
      return Toast.show('您可借最大金额为200000元')
    }
    if (+str < min) {
      setAmount(min)
      return Toast.show('最低可借金额为30000元起')
    }
  }

  const cityConfirm = (value: PickerValue[], extend: PickerValueExtend) => {
    const cityName = extend.items.map(item => String(item?.label || '')).join()
    setCityShow(cityName)
    if (value[0] === null) {
      return false
    }
    setCityValue(value)
    setHaveCityData(true)
  }

  const openView = (path: string) => {
    Storage.set(ENTER_AGREEMENT, true)
    window.location.href = `/${path}-agreement?isShowHeader=true`
  }

  // 姓名 / 身份证 / 城市填好后再展示职业与资产
  const isBasicInfoReady = useMemo(() => {
    const nameOk = !!realName && realName.length >= 2 && /^[\u4e00-\u9fa5]+$/.test(realName)
    const idOk = !!identity && validateIDNumber(identity)
    const cityOk = !!cityShow
    return nameOk && idOk && cityOk
  }, [realName, identity, cityShow])

  const extraInfoRef = useRef<HTMLDivElement>(null)
  const prevBasicReadyRef = useRef(false)

  useEffect(() => {
    if (isBasicInfoReady && !prevBasicReadyRef.current) {
      window.setTimeout(() => {
        extraInfoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
    prevBasicReadyRef.current = isBasicInfoReady
  }, [isBasicInfoReady])

  const confirm = async() => {
    if (!realName || realName.length < 2) {
      Toast.show('请输入真实姓名')
      return
    }
    if (!/^[\u4e00-\u9fa5]+$/.test(realName)) {
      Toast.show('真实姓名不能含有非中文字符')
      return
    }
    if (!identity || !validateIDNumber(identity)) {
      Toast.show('身份证号不正确，请重新输入')
      return
    }
    if (getAge(identity) < 18 || getAge(identity) > 60) {
      Toast.show('申请人年龄不能低于18岁或大于60岁')
      return
    }
    if (!cityShow) {
      Toast.show('请选择城市')
      return
    }
    if (!job || !jobLabel) {
      Toast.show('请选择职业身份')
      return
    }
    if (!noneOfAbove && !hasSelectedAsset()) {
      Toast.show('请至少选择一项资产，都没有请选择「以上都没有」')
      return
    }
    if (!checked) {
      setIsShake(true)
      setTimeout(() => setIsShake(false), 100)
      Toast.show('请勾选注册及隐私协议')
      return
    }

    const assets = buildAssetParams()
    const params: any = {
      realName,
      identity,
      workCityCode: cityValue.join(),
      workCity: cityShow,
      job,
      ...assets,
      adChannelCode,
    }
    if (validateIDNumber(identity)) {
      params.gender = getSex(identity)
      params.age = getAge(identity)
    }
    if (Number(amount) > 100000 && Number(amount) <= 200000) {
      params.applyLimit = 4
    } else {
      params.applyLimit = 3
    }

    try {
      setBtnLoading(true)
      await indexApi.tempSave(params)
      window.location.href = `/fill-information/waiting-result?options=${encodeURIComponent(JSON.stringify(params))}&sourceType=submit`
    } catch (error) {
      setBtnLoading(false)
    }
  }

  return (
    <div className={styles.fillInformation}>
      <div className={styles.promoBanner}>
        <div className={styles.promoBadge}>限时资格</div>
        <div className={styles.promoTop}>您已进入极速放款通道</div>
        <div className={styles.promoMain}>
          <div className={styles.promoText}>
            放款速度+80%
            <span className={styles.promoArrow}>↗</span>
          </div>
          <div className={styles.countdown}>
            <span className={styles.timeBox}>{hours}</span>
            <span className={styles.colon}>:</span>
            <span className={styles.timeBox}>{minutes}</span>
            <span className={styles.colon}>:</span>
            <span className={styles.timeBox}>{seconds}</span>
          </div>
        </div>
      </div>

      <div className={styles.priceBox}>
        <div className={styles.priceBoxTitle}>为您评估额度为 200000 (元）</div>
        <div className={styles.priceMain}>
          <div className={styles.priceMainL}>
            <div className={styles.priceMainLSymbol}>￥</div>
            <NumberInput
              className={styles.priceMainLNum}
              value={amount}
              onChange={amountOnChange}
              onBlur={amountOnBlur}
              numberFormat={{
                decimal: false,
                negative: false,
                leadingZero: false,
                maxLength: 6
              }}
              placeholder={''}
            />
          </div>
          <div className={styles.priceMainR} onClick={() => setAmount(max)}>全部借出</div>
        </div>
        <div className={styles.priceBoxTip}>年化利率<span> 7.2%-24%</span>，提前还无手续费</div>
      </div>

      <div className={styles.periodsBox} onClick={() => setPeriodsVisible(true)}>
        <div className={styles.periodsBoxTitle}>借多久</div>
        <div className={styles.rowRight}>
          <span className={styles.value}>{periodsValue}个月</span>
          <i className={styles.chevron} />
        </div>
      </div>

      <div className={styles.formCard}>
        <div className={styles.tipBanner}>填写真实信息，已有52.8万人成功下款</div>
        <div className={styles.formRow}>
          <span className={styles.label}>姓名</span>
          <Input
            className={styles.inputRight}
            maxLength={10}
            value={realName}
            placeholder="本人姓名"
            onChange={setRealName}
          />
        </div>
        <div className={styles.formRow}>
          <span className={styles.label}>身份证</span>
          <Input
            className={styles.inputRight}
            maxLength={18}
            value={identity}
            placeholder="本人身份证号"
            onChange={setIdentity}
          />
        </div>
        <div className={styles.formRow} onClick={() => setCityVisible(true)}>
          <span className={styles.label}>当前所在城市</span>
          <div className={styles.rowRight}>
            <span className={classNames(styles.value, !cityShow && styles.placeholder)}>
              {cityShow || '请选择'}
            </span>
            <i className={styles.chevron} />
          </div>
        </div>
      </div>

      {isBasicInfoReady && (
        <div className={styles.formCard} ref={extraInfoRef}>
          <div className={styles.tipBanner}>补充更多信息，通过率提升90%</div>
          <div className={styles.formRow} onClick={() => setJobVisible(true)}>
            <span className={styles.label}>职业身份<span className={styles.required}>*</span></span>
            <div className={styles.rowRight}>
              <span className={classNames(styles.value, !jobLabel && styles.placeholder)}>
                {jobLabel || '请选择'}
              </span>
              <i className={styles.chevron} />
            </div>
          </div>

          <div className={styles.assetSection}>
            <div className={styles.assetHeader} onClick={() => setAssetsExpanded(!assetsExpanded)}>
              <div className={styles.assetTitle}>
                <span>资产(多选)<span className={styles.required}>*</span></span>
                <span className={styles.assetSub}>资产<span className={styles.assetEm}>越多</span>，额度<span className={styles.assetEm}>越高</span></span>
              </div>
              <i className={classNames(styles.chevron, assetsExpanded && styles.up)} />
            </div>
            {assetsExpanded && (
              <div className={styles.assetTags}>
                {ASSET_OPTIONS.map(item => {
                  const active = !noneOfAbove && assetValues[item.key] === HAS_VALUE
                  return (
                    <div
                      key={item.key}
                      className={classNames(styles.tag, active && styles.active)}
                      onClick={() => handleAssetToggle(item.key)}
                    >
                      {item.label}
                    </div>
                  )
                })}
                <div
                  className={classNames(styles.tag, noneOfAbove && styles.active)}
                  onClick={handleNoneOfAbove}
                >
                  以上都没有
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.safeTip}>
        <img className={styles.safeIcon} src={require('@imgs/information/wft/safetyIcon.png')} alt="" />
        <span>国家级数据加密保护，仅用于贷款审核</span>
      </div>

      {isBasicInfoReady && (
        <>
          <div className={classNames(styles.agreement, isShake && styles.shake)}>
            <i className={classNames(styles.checked, checked && styles.active)} onClick={() => setChecked(!checked)} />
            阅读并同意
            <span className={styles.primary} onClick={() => openView('register')}>《用户注册服务协议》</span>
            和
            <span className={styles.primary} onClick={() => openView('privacy')}>《隐私政策》</span>
          </div>

          <Button
            block
            loading={btnLoading}
            loadingText="机构匹配中"
            className={styles.submitBtn}
            shape="rounded"
            color="primary"
            onClick={confirm}
          >
            提交借款
          </Button>
        </>
      )}

      <Picker
        columns={professionList}
        visible={jobVisible}
        onClose={() => setJobVisible(false)}
        onConfirm={(val, extend) => {
          const nextJob = (val?.[0] as string) || ''
          if (!job && nextJob) {
            setAmount((prev: number) => Number(prev) + 5000)
          }
          setJob(nextJob)
          setJobLabel(String(extend.items?.[0]?.label || ''))
        }}
      />
      <Picker
        columns={periodsColumns}
        visible={periodsVisible}
        defaultValue={[periodsValue]}
        onClose={() => setPeriodsVisible(false)}
        onConfirm={val => setPeriodsValue(val[0] || '12')}
      />
      <CascadePicker
        options={cityDate}
        visible={cityVisible}
        onClose={() => setCityVisible(false)}
        value={cityValue}
        onConfirm={cityConfirm}
      />
    </div>
  )
}

export default FillInformation
